import {
  Header,
  HeaderActions,
  HeaderIcon,
  HeaderTitle,
} from '@/ux-common/components/header/header';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';

import { AddStageButton } from './add-stage-button';

interface SequencerSectionTemplateProps {
  /** Table and form content */
  children: React.ReactNode;
  /** Optional icon displayed in the accordion trigger */
  icon?: React.ReactNode;
  /** Legend component displayed below the table */
  legend: React.ReactNode;
  /** Handler for adding a new stage - renders AddStageButton when provided */
  onAddStage?: () => void;
  /** Optional custom right buttons - overrides default AddStageButton */
  rightButtons?: React.ReactNode;
  /** Section title displayed in the accordion trigger */
  title: string;
}

/**
 * SequencerSectionTemplate provides a consistent accordion layout for sequencer editors.
 *
 * Uses composition pattern:
 * - Pass `onAddStage` for default AddStageButton behavior
 * - Pass `rightButtons` to fully customize the header actions
 *
 * @example
 * // Default with AddStageButton
 * <SequencerSectionTemplate
 *   title="Condenser Sequencer"
 *   legend={<SequencerLegend />}
 *   onAddStage={handleAddStage}
 * >
 *   {tableContent}
 * </SequencerSectionTemplate>
 *
 * @example
 * // Custom right buttons
 * <SequencerSectionTemplate
 *   title="Condenser Sequencer"
 *   legend={<SequencerLegend />}
 *   rightButtons={<CustomActions />}
 * >
 *   {tableContent}
 * </SequencerSectionTemplate>
 */
export const SequencerSectionTemplate = ({
  children,
  icon,
  legend,
  onAddStage,
  rightButtons,
  title,
}: SequencerSectionTemplateProps) => {
  // Use custom rightButtons if provided, otherwise render AddStageButton if onAddStage is provided
  const headerRightButtons =
    rightButtons ?? (onAddStage ? <AddStageButton onClick={onAddStage} /> : undefined);

  return (
    <Stack>
      <Header>
        {icon && <HeaderIcon>{icon}</HeaderIcon>}
        <HeaderTitle>
          <Typography as="h2" className="text-header-sub">
            {title}
          </Typography>
        </HeaderTitle>
        {headerRightButtons && <HeaderActions>{headerRightButtons}</HeaderActions>}
      </Header>

      <div className="relative">{children}</div>
      {legend}
    </Stack>
  );
};
